import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useBrowser } from "components/layout/BrowserContext";
import { addSnackBar } from "actions/snack-bar-actions";
import RtrImage from "components/source/shared/rtr-image";
import ClosableModal from "components/source/shared/closable-modal";
import { useUrl } from "components/layout/URLContext";
import { MODAL_ANIMATION_DURATION } from "rtr-constants";
import RTRButton from "components/design-system/rtr-button";
import { useAppSelector } from "../../../../../../util/store-types";

const ShortlistShareModal = ({
  isOpen,
  onRequestClose,
  url,
}: {
  isOpen: boolean;
  onRequestClose: () => void;
  url: string;
}) => {
  const dispatch = useDispatch();
  const [clicked, setClicked] = useState(false);
  const buttonText = clicked ? "Copied!" : "Copy Link";

  useEffect(
    function onClickedChange() {
      if (clicked) {
        const timer = setTimeout(() => {
          setClicked(false);
        }, 2000);
        return () => clearTimeout(timer);
      }
    },
    [clicked]
  );

  const copyLinkToClipboard = useCallback(() => {
    navigator.clipboard
      .writeText(url)
      .then(() => setClicked(true))
      .catch(error => {
        if (
          error &&
          typeof error === "object" &&
          "name" in error &&
          (error as { name: string }).name === "AbortError"
        ) {
          // user cancelled the share operation, no need to report
          return;
        }
        dispatch(
          addSnackBar({ copyPrimary: "There was an error sharing the link to this Shortlist.", duration: 5000 })
        );
        console.error("Error sharing shortlist link:", error);
      });
  }, [url, dispatch]);
  return (
    <ClosableModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      closeTimeoutMS={MODAL_ANIMATION_DURATION}
      optionalClass="animate-modal">
      <div>
        <h3 className="font-sans font-medium text-center">Share Your List</h3>
        <p className="font-sans text-center">Copy the link to send it to a friend or share on socials!</p>
        <div className="w-100 flex justify-between items-center mt-4">
          <input
            className="!bg-lighterGray grow !text-right !h-auto !px-6 !py-2 !rounded-md"
            name="shortlist-url"
            readOnly={true}
            value={url}></input>
          <RTRButton className="ml-2" onClick={() => copyLinkToClipboard()}>
            {buttonText}
          </RTRButton>
        </div>
      </div>
    </ClosableModal>
  );
};
export const ShortlistShare = () => {
  const baseUrl = useUrl();
  const { isMobileViewport } = useBrowser();
  const shortlistId = useAppSelector(state => state.shortlistId);
  const shortlistName = useAppSelector(state => state.gridMetaData.title);
  const url = (baseUrl ? new URL(baseUrl).origin : "") + "/shortlist/" + shortlistId;
  const [open, setOpen] = useState(false);

  const triggerShare = useCallback(() => {
    const payload = {
      title: shortlistName,
      text: `Check out this shortlist on Rent The Runway: ${shortlistName}`,
      url,
    };
    // try to use the native share API on mobile devices
    if (navigator.canShare?.(payload) && isMobileViewport) {
      navigator.share(payload);
    } else {
      // show the share modal
      setOpen(true);
    }
  }, [isMobileViewport, shortlistName, url, setOpen]);

  const onRequestClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  return (
    <>
      {open && <ShortlistShareModal isOpen={open} onRequestClose={onRequestClose} url={url} />}
      <div className="flex justify-end items-center">
        <button
          onClick={() => triggerShare()}
          data-test-id="product-collection-visitor-actions-share"
          className="
            cursor-pointer
            h-8 w-8
            
            // --- Core Layout & Stacking ---
            relative          // Needed for positioning children absolutely
            flex items-center justify-center
                
            // --- Before Pseudo-Element Styling ---
            before:content-['']
            before:absolute
            before:inset-0     // A simpler way to say top-0, left-0, right-0, bottom-0
            before:bg-lighterGray
            
            // --- Initial State & Transition ---
            before:rounded-full
            before:scale-0
            before:transition-transform
            before:duration-300
            before:ease-in-out
            before:origin-center 
            
            // --- Hover State ---
            hover:before:scale-[1.2]
        ">
          <RtrImage src="/images/sprites/reb/svg/ic_share.svg" alt="Share" className="h-6 w-6 z-10" />
        </button>
      </div>
    </>
  );
};
